import type { RouteStop, RouteStopStatus } from '../models/RouteStop';
import { normalizeRouteStop } from '../models/RouteStop';
import { sortStopsBySequence } from './routeOptimize';

export function isStopCleared(stop: Pick<RouteStop, 'runnerAmountConfirmed' | 'listerAmountConfirmed' | 'status'>): boolean {
  if (stop.status === 'skipped') return false;
  return Boolean(stop.runnerAmountConfirmed && stop.listerAmountConfirmed);
}

export function resolveStopStatus(stop: Pick<
  RouteStop,
  'status' | 'runnerAmountConfirmed' | 'listerAmountConfirmed' | 'skipReason' | 'skippedBy'
>): RouteStopStatus {
  if (stop.status === 'skipped' || stop.skipReason || stop.skippedBy) return 'skipped';
  if (stop.runnerAmountConfirmed && stop.listerAmountConfirmed) return 'cleared';
  if (stop.runnerAmountConfirmed || stop.listerAmountConfirmed) return 'awaiting_clear';
  if (stop.status === 'in_progress') return 'in_progress';
  return 'pending';
}

/** Idempotent merge: never downgrade an ack true→false; keep first non-null amounts. */
export function mergeStopAcks(
  local: Partial<RouteStop> | Record<string, unknown>,
  remote: Partial<RouteStop> | Record<string, unknown>,
): RouteStop {
  const a = normalizeRouteStop(local as RouteStop);
  const b = normalizeRouteStop(remote as RouteStop);
  const base = { ...a, ...b, id: a.id || b.id };

  const runnerAmountConfirmed = Boolean(a.runnerAmountConfirmed || b.runnerAmountConfirmed);
  const listerAmountConfirmed = Boolean(a.listerAmountConfirmed || b.listerAmountConfirmed);

  const collectedAmount =
    a.runnerAmountConfirmed ? a.collectedAmount
      : b.runnerAmountConfirmed ? b.collectedAmount
        : (a.collectedAmount ?? b.collectedAmount ?? null);

  const listerDeliveredAmount =
    a.listerAmountConfirmed ? a.listerDeliveredAmount
      : b.listerAmountConfirmed ? b.listerDeliveredAmount
        : (a.listerDeliveredAmount ?? b.listerDeliveredAmount ?? null);

  const skipReason = a.skipReason || b.skipReason || null;
  const skippedBy = a.skippedBy || b.skippedBy || null;
  const skipped = Boolean(skipReason || skippedBy || a.status === 'skipped' || b.status === 'skipped');

  const merged: RouteStop = normalizeRouteStop({
    ...base,
    expectedAmount: a.expectedAmount || b.expectedAmount,
    addr: a.addr || b.addr,
    lat: a.lat ?? b.lat,
    lng: a.lng ?? b.lng,
    runnerAmountConfirmed,
    listerAmountConfirmed,
    collectedAmount,
    listerDeliveredAmount,
    runnerConfirmedAt: a.runnerConfirmedAt ?? b.runnerConfirmedAt ?? null,
    listerConfirmedAt: a.listerConfirmedAt ?? b.listerConfirmedAt ?? null,
    skipReason,
    skippedBy,
    status: skipped ? 'skipped' : 'pending',
    incidentNote: a.incidentNote || b.incidentNote || null,
    incidentAt: a.incidentAt ?? b.incidentAt ?? null,
    priority: a.priority === 'high' || b.priority === 'high' ? 'high' : (a.priority ?? b.priority ?? 'normal'),
    sequence: a.sequence ?? b.sequence ?? null,
  });

  const status = skipped ? 'skipped' : resolveStopStatus(merged);
  return {
    ...merged,
    status,
    done: status === 'cleared',
    collectedAt: status === 'cleared'
      ? (merged.collectedAt
        ?? (Math.max(merged.runnerConfirmedAt ?? 0, merged.listerConfirmedAt ?? 0) || Date.now()))
      : merged.collectedAt,
  };
}

export function currentStopIndex(stops: RouteStop[]): number {
  const normalized = sortStopsBySequence(stops);
  return normalized.findIndex(
    (s) => s.status !== 'skipped' && s.status !== 'cleared' && !s.runnerAmountConfirmed,
  );
}

export function getCurrentStop(stops: RouteStop[]): RouteStop | null {
  const ordered = sortStopsBySequence(stops);
  const idx = currentStopIndex(ordered);
  return idx < 0 ? null : ordered[idx];
}

/** Runner may open/navigate to another pending stop only after confirming collected amount on current. */
export function canAdvanceToNextStop(stops: RouteStop[]): boolean {
  const current = getCurrentStop(stops);
  return current == null;
}

export function canOpenStop(stops: RouteStop[], stopId: string): boolean {
  const normalized = sortStopsBySequence(stops);
  const target = normalized.find((s) => s.id === stopId);
  if (!target) return false;
  if (target.status === 'skipped' || target.status === 'cleared') return true;
  if (target.runnerAmountConfirmed) return true;
  const current = getCurrentStop(normalized);
  return current == null || current.id === stopId;
}

export function applyRunnerConfirm(
  stop: RouteStop,
  collectedAmount: number,
  at = Date.now(),
): RouteStop {
  const next = normalizeRouteStop({
    ...stop,
    collectedAmount,
    runnerAmountConfirmed: true,
    runnerConfirmedAt: at,
    status: 'awaiting_clear',
  });
  // House / ad-hoc stops have no lister handoff — clear with runner ack alone.
  if (next.kind === 'house' || next.kind === 'adhoc') {
    return normalizeRouteStop({
      ...next,
      listerAmountConfirmed: true,
      listerDeliveredAmount: collectedAmount,
      listerConfirmedAt: at,
      status: 'cleared',
      collectedAt: at,
      done: true,
    });
  }
  const status = resolveStopStatus(next);
  return {
    ...next,
    status,
    done: status === 'cleared',
    collectedAt: status === 'cleared' ? at : next.collectedAt,
  };
}

export function applyListerConfirm(
  stop: RouteStop,
  listerDeliveredAmount: number,
  at = Date.now(),
): RouteStop {
  const next = normalizeRouteStop({
    ...stop,
    listerDeliveredAmount,
    listerAmountConfirmed: true,
    listerConfirmedAt: at,
    status: 'awaiting_clear',
  });
  const status = resolveStopStatus(next);
  return {
    ...next,
    status,
    done: status === 'cleared',
    collectedAt: status === 'cleared' ? at : next.collectedAt,
  };
}

/**
 * Banker/god self-collect when there is no runner: mark the lister-registered
 * expected amount as collected and secured (dual-ack clear in one step).
 */
export function applyBankerSecureCollect(
  stop: RouteStop,
  actorId: string,
  at = Date.now(),
  amount?: number,
): RouteStop {
  const secured = Number.isFinite(amount as number)
    ? Math.max(0, Number(amount))
    : Math.max(0, Number(stop.expectedAmount ?? 0));
  return normalizeRouteStop({
    ...stop,
    collectedAmount: secured,
    listerDeliveredAmount: secured,
    runnerAmountConfirmed: true,
    listerAmountConfirmed: true,
    runnerConfirmedAt: at,
    listerConfirmedAt: at,
    runnerId: stop.runnerId || actorId,
    status: 'cleared',
    done: true,
    collectedAt: at,
    securedBy: actorId,
    securedAt: at,
    securedByRole: 'banker',
  });
}

/** Open official/adhoc stop that banker may secure-collect during an authorized run. */
export function canBankerSecureCollect(stop: RouteStop): boolean {
  if (stop.status === 'cleared' || stop.status === 'skipped') return false;
  if (stop.kind === 'house') return false;
  return true;
}

export function applyBankerSkip(
  stop: RouteStop,
  reason: string,
  skippedBy: string,
): RouteStop {
  return normalizeRouteStop({
    ...stop,
    status: 'skipped',
    skipReason: reason,
    skippedBy,
    done: false,
  });
}

/** Official pickup / HQ stops (not banker ad-hoc). */
export function isOfficialRouteStop(stop: RouteStop): boolean {
  return stop.kind !== 'adhoc';
}

/** Ad-hoc stops can be removed from the route; official ones are skipped instead. */
export function canDeleteRouteStop(stop: RouteStop): boolean {
  return stop.kind === 'adhoc'
    && stop.status !== 'cleared'
    && stop.status !== 'skipped';
}

/** Official stops (lister / house) may be skipped by the banker. */
export function canSkipRouteStop(stop: RouteStop): boolean {
  return isOfficialRouteStop(stop)
    && stop.status !== 'cleared'
    && stop.status !== 'skipped';
}

/**
 * Lister pickups with nothing to collect are auto-skipped when departure is authorized.
 * House HQ stays (destination). Ad-hoc stays unless banker deletes it.
 */
export function shouldAutoSkipOnDeparture(stop: RouteStop): boolean {
  if (stop.status === 'cleared' || stop.status === 'skipped') return false;
  if (stop.kind === 'house' || stop.kind === 'adhoc') return false;
  // Missing kind + listerId ⇒ treat as official lister (seeded legacy).
  if (stop.kind != null && stop.kind !== 'lister') return false;
  return Number(stop.expectedAmount ?? 0) <= 0;
}

export function applyAutoSkipZeroExpected(
  stops: RouteStop[],
  skippedBy: string,
  reason = 'no_collectable',
): RouteStop[] {
  return stops.map((stop) => (
    shouldAutoSkipOnDeparture(stop)
      ? applyBankerSkip(stop, reason, skippedBy)
      : stop
  ));
}

export function applyIncident(
  stop: RouteStop,
  note: string,
  at = Date.now(),
): RouteStop {
  return normalizeRouteStop({
    ...stop,
    incidentNote: note,
    incidentAt: at,
  });
}

export function amountsDiffer(stop: RouteStop): boolean {
  if (stop.collectedAmount == null || stop.expectedAmount == null) return false;
  return Number(stop.collectedAmount) !== Number(stop.expectedAmount);
}

export function dualAckDiscrepancy(stop: RouteStop): boolean {
  if (!stop.runnerAmountConfirmed || !stop.listerAmountConfirmed) return false;
  if (stop.collectedAmount == null || stop.listerDeliveredAmount == null) return false;
  return Number(stop.collectedAmount) !== Number(stop.listerDeliveredAmount);
}

/** Open dual-ack mismatch that banker has not yet acknowledged. */
export function canReviewDiscrepancy(stop: RouteStop): boolean {
  return dualAckDiscrepancy(stop) && !stop.discrepancyReviewedAt;
}

export function applyDiscrepancyReview(
  stop: RouteStop,
  actorId: string,
  at = Date.now(),
  note?: string | null,
): RouteStop {
  return normalizeRouteStop({
    ...stop,
    discrepancyReviewedAt: at,
    discrepancyReviewedBy: actorId,
    discrepancyNote: note != null && String(note).trim()
      ? String(note).trim()
      : (stop.discrepancyNote ?? null),
  });
}

export {
  expectedAmountFromPlays,
  generateStopsFromListers,
  isStopCollectedLike,
  type ListerSeed,
  type PlayStakeLike,
} from './routeStopGeneration';

/** Departure authorization gates for runner/lister confirm actions. */
export {
  canListerConfirmHandoff,
  canRunnerConfirmStop,
  isRouteDepartureAuthorized,
} from './routeDeparture';
